import { BaseSkeleton } from '../../../types';

export type ContactTier = 'inner_circle' | 'tier1' | 'tier2' | 'tier3' | 'dormant';

export type ContactSignalType =
  | 'job_change'
  | 'email_activity'
  | 'calendar_meeting'
  | 'linkedin_post'
  | 'reddit_dm'
  | 'slack_dm'
  | 'twitter_dm'
  | 'twitter_mention'
  | 'github_activity'
  | 'custom';

export type ContactChannel =
  | 'email'
  | 'calendar'
  | 'slack'
  | 'whatsapp'
  | 'phone'
  | 'linkedin'
  | 'reddit'
  | 'twitter'
  | 'github'
  | 'other';

export type ContactIdentityType = 'person' | 'account' | 'ai' | 'unknown';
export type InteractionDirection = 'inbound' | 'outbound' | 'both';

export interface ContactInteraction {
  _id: string;
  contactId: string;
  at: string;               // ISO date
  channel: ContactChannel;
  direction: InteractionDirection;
  snippet?: string;
  source: string;           // "gmail" | "slack" | "reddit" | "manual" | ...
  externalId?: string;      // dedup key from source system
  created_at?: string;
}

export interface ContactSignal {
  id: string;
  type: ContactSignalType;
  title: string;
  body?: string;
  detectedAt: string;
  source: string;
}

export interface ContactLastInteraction {
  at: string;
  channel: ContactChannel;
  snippet?: string;
}

export interface ContactHandles {
  reddit?: string;          // "u/username"
  twitter?: string;         // "@handle" or "handle"
  slack?: string;           // Slack user ID e.g. "U012AB3CD"
  slackWorkspace?: string;  // workspace name / domain
  github?: string;          // "username"
}

export interface ContactSkeleton extends BaseSkeleton {
  '@type': 'Contact';

  // Basic info
  email?: string;
  phone?: string;
  company?: string;
  title?: string;
  avatarUrl?: string;

  // Socials (legacy flat fields — kept for backward compat; prefer handles.*)
  linkedinUrl?: string;
  twitterHandle?: string;
  githubHandle?: string;

  // Multi-channel handles
  handles?: ContactHandles;

  // Relationship management
  tier: ContactTier;
  tags?: string[];
  notes?: string;

  // Identity classification
  identityType?: ContactIdentityType;
  relatedContactIds?: string[];

  // Primary channel context (shows in header)
  primaryChannel?: ContactChannel;
  primaryChannelContext?: string;   // e.g. "Acme Inc Slack" | "r/entrepreneur"

  // Interaction tracking
  lastInteraction?: ContactLastInteraction;
  preferredChannel?: ContactChannel;

  // Warmth score (computed, cached)
  warmthScore?: number;             // 0–1
  warmthUpdatedAt?: string;

  // AI-generated intelligence
  topics?: string[];
  signals?: ContactSignal[];
  nextReachOutAt?: string;
  nextReachOutReason?: string;

  // Integration cross-refs
  sources?: string[];
  lastEnrichedAt?: string;
}

export interface ContactEntity {
  _id: string;
  id?: string;
  entityType: 'Contact';
  name: string;
  spaceId?: string;
  created_at: string;
  updated_at?: string;
  skeleton: ContactSkeleton;
}

export const TIER_LABELS: Record<ContactTier, string> = {
  inner_circle: 'Inner Circle',
  tier1:        'Tier 1',
  tier2:        'Tier 2',
  tier3:        'Tier 3',
  dormant:      'Dormant',
};

export const TIER_DESCRIPTIONS: Record<ContactTier, string> = {
  inner_circle: 'Drop everything — respond instantly',
  tier1:        'Respond soon (within hours)',
  tier2:        'Respond within a day or two',
  tier3:        'Respond within the week',
  dormant:      'Low-priority or inactive',
};

/** Days before a contact of each tier is considered stale */
export const TIER_CADENCE_DAYS: Record<ContactTier, number | null> = {
  inner_circle: 7,
  tier1:        14,
  tier2:        30,
  tier3:        60,
  dormant:      null,
};

export function getWarmthLevel(score: number): 'hot' | 'warm' | 'cool' | 'cold' {
  if (score > 0.7) return 'hot';
  if (score > 0.4) return 'warm';
  if (score > 0.1) return 'cool';
  return 'cold';
}

/** Normalize any legacy or alternate tier values to the canonical 5-tier system */
export function normalizeTier(tier: string): ContactTier {
  switch (tier) {
    case 'inner_circle': return 'inner_circle';
    case 'tier1':        return 'tier1';
    case 'tier2':        return 'tier2';
    case 'tier3':        return 'tier3';
    case 'dormant':      return 'dormant';
    // Map new simplified tiers back to canonical
    case 'close':        return 'inner_circle';
    case 'regular':      return 'tier2';
    case 'archived':     return 'dormant';
    default:             return 'tier3';
  }
}
